/**
 * tadilaTalepService.js — Bölüm 25-A/B
 *
 * Admin endpoint'leri: /api/v1/tadilat-talep/...   (auth gerekir)
 * Public endpoint'ler: /public/tadilat/...          (token tabanlı, auth yok)
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader, getPublicHeader } from '../Components/Contexts/BackendConstants';

const ADMIN_BASE  = `${JAVA_BACKEND_API_URL}/tadilat-talep`;
// /public/* path'leri /api/v1 prefix'i olmadan:
const PUBLIC_BASE = JAVA_BACKEND_API_URL.replace('/api/v1', '') + '/public/tadilat';

// ─── Admin — Token Yönetimi ───────────────────────────────────────────────────

/**
 * Token oluştur / yenile.
 * PUT /api/v1/tadilat-talep/token/{fuarId}
 * ⚠️ Yenileme eski public linkleri geçersiz kılar.
 */
export async function generateToken(fuarId) {
  const r = await axios.put(
    `${ADMIN_BASE}/token/${fuarId}`,
    {},
    { headers: getAuthHeader() }
  );
  return r.data; // { result, data: { token }, message }
}

/**
 * Mevcut token'ı getir.
 * GET /api/v1/tadilat-talep/token/{fuarId}
 * 404 → henüz token oluşturulmamış.
 */
export async function getToken(fuarId) {
  const r = await axios.get(
    `${ADMIN_BASE}/token/${fuarId}`,
    { headers: getAuthHeader() }
  );
  return r.data; // { result, data: { token }, message }
}

// ─── Admin — Başvuru (Submission) Yönetimi ────────────────────────────────────

/**
 * Başvuru listesi (paginated).
 * GET /api/v1/tadilat-talep/submissions
 */
export async function getSubmissions({ fuarId, standId, page = 0, limit = 25 } = {}) {
  const r = await axios.get(`${ADMIN_BASE}/submissions`, {
    headers: getAuthHeader(),
    // API 1-tabanlı sayfalama kullanıyor (page=1 → ilk sayfa)
    params: { fuarId, standId, page: page + 1, limit },
  });
  return r.data; // { total, page, limit, records: [] }
}

/**
 * Tekil başvuru detayı.
 * GET /api/v1/tadilat-talep/submissions/{id}
 */
export async function getSubmission(id) {
  const r = await axios.get(
    `${ADMIN_BASE}/submissions/${id}`,
    { headers: getAuthHeader() }
  );
  return r.data; // { result, data: { ...submissionFields }, message }
}

/**
 * Başvuru sil — kalıcı silme, onay dialogu göstermek UI sorumluluğu.
 * DELETE /api/v1/tadilat-talep/submissions/{id}
 */
export async function deleteSubmission(id) {
  const r = await axios.delete(
    `${ADMIN_BASE}/submissions/${id}`,
    { headers: getAuthHeader() }
  );
  return r.data;
}

// ─── Public — Token Tabanlı (auth gerekmez) ───────────────────────────────────

/**
 * Token doğrula + fuar/salon/stand dropdown verileri getir.
 * GET /public/tadilat/fair-data?token=...
 * Response: { fuarAdi, salonlar: [{ id, adi, standlar: [{ id, standNo }] }] }
 */
export async function getFairData(token) {
  console.log('[tadilaTalepService] getFairData → gerçek API çağrısı, token:', token);
  const r = await axios.get(`${PUBLIC_BASE}/fair-data`, {
    params: { token },
    headers: getPublicHeader(),
  });
  const raw = r.data;
  console.log('[tadilaTalepService] getFairData RAW response:', JSON.stringify(raw));

  // Olası wrapper'ları aç
  const payload = raw?.data ?? raw;

  // Salonlar: backend Türkçe (salonlar) veya İngilizce (halls/salons) kullanıyor olabilir
  const salonlar =
    payload?.salonlar ??
    payload?.halls ??
    payload?.salons ??
    payload?.salon_list ??
    [];

  // Standlar: backend Türkçe (standlar) veya İngilizce (stands/stantlar) kullanıyor olabilir
  const standlar =
    payload?.standlar ??
    payload?.stands ??
    payload?.stantlar ??
    payload?.stand_list ??
    [];

  const normalized = {
    fuarId:  payload?.fuarId  ?? payload?.fuar_id  ?? null,
    fuarAdi: payload?.fuarAdi ?? payload?.fuar_adi ?? payload?.fuarName ?? '',
    salonlar: Array.isArray(salonlar) ? salonlar.map(s => ({
      salonId:  s.salonId  ?? s.salon_id  ?? s.id,
      salonAdi: s.salonAdi ?? s.salon_adi ?? s.salonName ?? s.name ?? `Salon #${s.salonId ?? s.id}`,
    })) : [],
    standlar: Array.isArray(standlar) ? standlar.map(s => ({
      standId: s.standId  ?? s.stand_id  ?? s.id,
      standNo: s.standNo  ?? s.stand_no  ?? s.standName ?? s.name ?? `Stand #${s.standId ?? s.id}`,
      salonId: s.salonId  ?? s.salon_id  ?? s.hallId ?? null,
    })) : [],
  };

  console.log('[tadilaTalepService] getFairData normalized:', normalized);
  return normalized;
}

/**
 * Tadilat talep formunu gönder.
 * POST /public/tadilat/submit
 */
export async function submitTadilatTalep(body) {
  console.log('[tadilaTalepService] submitTadilatTalep → gerçek API çağrısı, body:', body);
  const r = await axios.post(`${PUBLIC_BASE}/submit`, body, {
    headers: getPublicHeader(),
  });
  console.log('[tadilaTalepService] submitTadilatTalep response:', r.data);
  return r.data;
}

